import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Rx';
import { Store } from '@ngrx/store';
import { map, catchError } from 'rxjs/operators';
import { omit, get, find, isEmpty } from 'lodash';
import * as uuid from 'uuid';
import { Storage } from '@ionic/storage';
import { Http } from '@angular/http';
import { ApiGateway } from './api-gateway.service';
import { SessionAttributionService } from './session-attribution.service';
import { AppState } from './index';

const storageConfig = {
  name: '__PMdb',
  storeName: 'appStore',
  driverOrder: ['sqlite', 'indexeddb', 'localstorage'],
};
const storage = new Storage(storageConfig);

declare var Penmate;
declare var gtag;

const SESSION_ATTRIBUTION_PAYLOAD_FIELDS = [
  'session_acquisition_source',
  'session_acquisition_channel',
  'guide_landing_path',
  'guide_page_segment',
  'guide_source_task',
  'guide_cta_id',
  'guide_cta_position',
  'guide_component',
  'facility_slug',
  'session_attribution',
  'metrics_context',
];

@Injectable({ providedIn: 'root' })
export class EventService {
  sessionId;

  constructor(
    public apiClient: ApiGateway,
    private http: Http,
    private store: Store<AppState>,
    private sessionAttribution: SessionAttributionService,
  ) {}

  start = async (payload = {}) => {
    const attribution = this.sessionAttribution.captureFromUrl();
    const eventPayload = this.withoutSessionAttribution(payload);
    const deviceId = await this.getDeviceId();
    this.sessionId = new Date().getTime();
    return this.apiClient.post(
      `${Penmate.env.apiUrl}/metrics/start`,
      {},
      {
        event: 'start',
        payload: {
          ...attribution,
          ...eventPayload,
          device_id: deviceId,
          session_id: this.sessionId,
        },
      },
    );
  };

  gaUserData =  data => {
    if (isEmpty(data)) {
      return;
    }
    gtag('set', 'user_data', data);
  }

  gaEventTrack = (event, payload = {}) => {
    gtag('event', event, payload);
  }
  
  gtmEvent = (eventName, payload = {}) => {
    try {
      if (typeof window === 'undefined') return;
      const win = window as any;
      win.dataLayer = win.dataLayer || [];
      win.dataLayer.push({ ...payload, event: eventName });
    } catch {
      // Never block app flows on GTM errors.
    }
  }

  trackSendLetterConversion = (payload: { value?: number; currency?: string; transaction_id?: string } = {}) => {
    const value = typeof payload.value === 'number' && Number.isFinite(payload.value) ? payload.value : 0;
    const currency = typeof payload.currency === 'string' && payload.currency ? payload.currency : 'USD';
    this.gtmEvent('send_letter', {
      value,
      currency,
      transaction_id: payload.transaction_id || undefined,
    });
  }

  trackAddedPenmateConversion = (payload: { penmate_id?: string | number } = {}) => {
    this.gtmEvent('added_penmate', {
      penmate_id: payload.penmate_id || undefined,
    });
  }

  track = async (event, payload = {}) => {
    const deviceId = await this.getDeviceId();
    const attribution = event === 'add-metrics' ? {} : this.sessionAttribution.props();
    const metricPayload = this.withoutSessionAttribution(payload);
    const penmate_id = get(metricPayload, 'penmate_id');
    let tz = metricPayload['timezone'];
    if (!tz) {
      try {
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      } catch {
        /* Intl.DateTimeFormat not supported */
      }
    }
    if (!penmate_id) {
      return this.apiClient.post(
        `${Penmate.env.apiUrl}/metrics`,
        {},
        {
          event,
          payload: {
            timezone: tz,
            ...metricPayload,
            ...attribution,
            device_id: deviceId,
            session_id: this.sessionId
          },
        },
      );
    }

    return new Promise((resolve, reject) => {
      this.store
        .select(state => ({
          penmates: state.myPenmates.penmates,
          currentPenmate: state.myPenmates.currentPenmate,
        }))
        .subscribe(async ({ penmates, currentPenmate }) => {
          const eventPayload = {
            event,
            payload: {
              ...metricPayload,
              ...attribution,
              device_id: deviceId,
              session_id: this.sessionId,
              penmate: {},
            },
          };
          const penmate =
            find(penmates, ({ id }) => id === parseInt(penmate_id, 10)) || currentPenmate;
          if (penmate_id && penmate) {
            eventPayload.payload.penmate = {
              id: penmate.id,
              first_name: penmate.first_name,
              last_name: penmate.last_name,
              name: penmate.name,
              slug: penmate.slug,
              facility: penmate.facility,
            };
          } else {
            delete eventPayload.payload.penmate;
          }
          try {
            const res = await this.apiClient.post(
              `${Penmate.env.apiUrl}/metrics`,
              {},
              eventPayload,
            );
            resolve(res);
          } catch (e) {
            reject(e);
          }
        })
        .unsubscribe();
    });
  };

  private withoutSessionAttribution(payload = {}) {
    return omit(payload || {}, SESSION_ATTRIBUTION_PAYLOAD_FIELDS);
  }

  getDeviceId = async () => {
    const deviceId = await storage.get('deviceId');
    if (deviceId) {
      return deviceId;
    }
    const nextDeviceId = uuid.v4();
    await storage.set('deviceId', nextDeviceId);
    return nextDeviceId;
  };
}
